import { defineStore } from 'pinia'
import Keycloak from 'keycloak-js'
import { ref, computed } from 'vue'

export const LOGIN_RETURN_TO_KEY = 'mijunta.login.returnTo'

const keycloak = new Keycloak({
  url: import.meta.env.VITE_KEYCLOAK_URL,
  realm: import.meta.env.VITE_KEYCLOAK_REALM,
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
})

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | undefined>(undefined)
  const idTokenParsed = ref<Record<string, unknown> | undefined>(undefined)
  const initialized = ref(false)
  let initPromise: Promise<boolean> | undefined
  let logoutInProgress = false

  const isAuthenticated = computed(() => !!token.value)
  const userProfile = computed(() => idTokenParsed.value)
  const juntaId = computed<number | undefined>(() => {
    const id = idTokenParsed.value?.['junta_id']
    if (typeof id === 'number' && Number.isFinite(id)) return id
    if (typeof id === 'string' && id.trim().length > 0) {
      const parsed = Number(id)
      return Number.isFinite(parsed) ? parsed : undefined
    }
    return undefined
  })

  function hasRole(role: string): boolean {
    const parsed = keycloak.tokenParsed as Record<string, unknown> | undefined
    if (!parsed || !role) return false

    const wanted = role.trim().toUpperCase()
    if (!wanted) return false

    const allRoles = new Set<string>()

    const realmAccess = parsed['realm_access']
    if (realmAccess && typeof realmAccess === 'object') {
      const realmRoles = (realmAccess as { roles?: unknown }).roles
      if (Array.isArray(realmRoles)) {
        for (const candidate of realmRoles) {
          if (typeof candidate === 'string') allRoles.add(candidate.trim().toUpperCase())
        }
      }
    }

    const resourceAccess = parsed['resource_access']
    if (resourceAccess && typeof resourceAccess === 'object') {
      for (const value of Object.values(resourceAccess as Record<string, unknown>)) {
        if (!value || typeof value !== 'object') continue
        const clientRoles = (value as { roles?: unknown }).roles
        if (!Array.isArray(clientRoles)) continue
        for (const candidate of clientRoles) {
          if (typeof candidate === 'string') allRoles.add(candidate.trim().toUpperCase())
        }
      }
    }

    return allRoles.has(wanted)
  }

  function syncSession(): void {
    token.value = keycloak.token
    idTokenParsed.value = keycloak.idTokenParsed as Record<string, unknown> | undefined
  }

  function clearSession(): void {
    token.value = undefined
    idTokenParsed.value = undefined
  }

  function redirectToLogin(): void {
    if (window.location.pathname === '/login') return

    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
    window.sessionStorage.setItem(LOGIN_RETURN_TO_KEY, currentPath)
    window.location.replace(`${window.location.origin}/login`)
  }

  function init(): Promise<boolean> {
    if (!initPromise) {
      keycloak.onAuthLogout = () => {
        clearSession()
        if (!logoutInProgress) redirectToLogin()
      }
      keycloak.onAuthRefreshSuccess = syncSession
      keycloak.onTokenExpired = () => {
        void keycloak.updateToken(60).then(syncSession).catch((error: unknown) => {
          clearSession()
          console.warn('No se pudo renovar la sesión con Keycloak.', error)
          redirectToLogin()
        })
      }

      initPromise = keycloak.init({
        checkLoginIframe: false,
        // Sin onLoad ni silentCheckSsoRedirectUri:
        // - Evita el check de 3rd-party cookies que bloqueaba el flujo OAuth
        // - Evita el prompt=none redirect que causaba bucle infinito
        // - keycloak-js procesa el código directamente si está en la URL
      }).then((authenticated) => {
        syncSession()
        return authenticated
      }).finally(() => {
        initialized.value = true
      })
    }

    return initPromise
  }

  function login(): void {
    // Usar el destino guardado como redirect_uri para que Keycloak vuelva directo
    const returnTo = window.sessionStorage.getItem(LOGIN_RETURN_TO_KEY)
    const redirectUri = returnTo
      ? `${window.location.origin}${returnTo}`
      : window.location.origin
    window.sessionStorage.removeItem(LOGIN_RETURN_TO_KEY)
    // init() debe llamarse antes de keycloak.login() — el adapter interno no
    // se configura hasta que init() se ejecuta. En /login nunca lo llama el router.
    void init().then(() => { keycloak.login({ redirectUri }) })
  }

  function logout(): void {
    clearSession()
    window.sessionStorage.removeItem(LOGIN_RETURN_TO_KEY)
    logoutInProgress = true
    void Promise.resolve(keycloak.logout({ redirectUri: `${window.location.origin}/login` })).catch(
      (error: unknown) => {
        logoutInProgress = false
        console.warn('No se pudo cerrar la sesión con Keycloak.', error)
        redirectToLogin()
      },
    )
  }

  async function getToken(): Promise<string> {
    if (!keycloak.token) return ''
    try {
      await keycloak.updateToken(30)
      syncSession()
    } catch (error) {
      clearSession()
      redirectToLogin()
      throw error
    }
    return keycloak.token ?? ''
  }

  return {
    token,
    isAuthenticated,
    userProfile,
    juntaId,
    initialized,
    hasRole,
    init,
    login,
    logout,
    getToken,
  }
})
